import type {Action} from '@github-ui/action-bar'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {AddToProjectsBulkAction} from '@github-ui/issues-bulk-actions/AddToProjectsBulkAction'
import {ApplyAssigneesBulkAction} from '@github-ui/issues-bulk-actions/ApplyAssigneesBulkAction'
import {ApplyIssueTypeBulkAction as ApplyIssueTypeAction} from '@github-ui/issues-bulk-actions/ApplyIssueTypeBulkAction'
import {ApplyLabelsBulkAction} from '@github-ui/issues-bulk-actions/ApplyLabelsBulkAction'
import {ApplyMilestoneBulkAction} from '@github-ui/issues-bulk-actions/ApplyMilestoneBulkAction'
import {BulkMarkAs} from '@github-ui/issues-bulk-actions/BulkMarkAs'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {useListViewMultiPageSelection} from '@github-ui/list-view/ListViewMultiPageSelectionContext'
import {useListViewSelection} from '@github-ui/list-view/ListViewSelectionContext'
import {noop} from '@github-ui/noop'
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {
  type Icon,
  IssueOpenedIcon,
  MilestoneIcon,
  PeopleIcon,
  ProjectSymlinkIcon,
  RocketIcon,
  TagIcon,
  TriangleDownIcon,
} from '@primer/octicons-react'
import {ActionList, Button} from '@primer/react'
import {Suspense, useCallback, useMemo, useState} from 'react'

import {BUTTON_LABELS} from '../../../constants/buttons'
import {LABELS} from '../../../constants/labels'
import {VALUES} from '../../../constants/values'
import {useQueryBulk, useQuerySearchState} from '../../../contexts/QueryContext'
import type {AppPayload} from '../../../types/app-payload'
import {guardOverflowMenuChange} from '../../../utils/guard-overflow-menu-change'
import type {IssueNodeType, ItemNodeType} from '../ListItems'
import type {ListItemsHeaderProps} from './ListItemsHeader'

function ListItemsHeaderBulkFallback() {
  const renderFake = useCallback((isOverflowing: boolean, LeadingVisual: Icon, anchorText: string) => {
    if (isOverflowing) {
      return (
        <ActionList.Item disabled>
          <ActionList.LeadingVisual>
            <LeadingVisual />
          </ActionList.LeadingVisual>
          {anchorText}
          <ActionList.TrailingVisual>
            <TriangleDownIcon />
          </ActionList.TrailingVisual>
        </ActionList.Item>
      )
    }

    return (
      <Button disabled leadingVisual={LeadingVisual} trailingVisual={TriangleDownIcon}>
        {anchorText}
      </Button>
    )
  }, [])

  // This array should be in sync with the actions array populating the actual component
  const actions = useMemo(
    () => [
      {
        key: 'mark-as',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, VALUES.issueIcons.CLOSED.icon, 'Mark as'),
      },
      {
        key: 'apply-labels',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, TagIcon, 'Label'),
      },
      {
        key: 'apply-assignees',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, PeopleIcon, 'Assign'),
      },
      {
        key: 'add-to-projects',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, ProjectSymlinkIcon, 'Project'),
      },
      {
        key: 'apply-milestone',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, MilestoneIcon, 'Milestone'),
      },
      {
        key: 'apply-issue-type',
        render: (isOverflowMenu: boolean) => renderFake(isOverflowMenu, IssueOpenedIcon, BUTTON_LABELS.setIssueType),
      },
    ],
    [renderFake],
  )

  return (
    <ListViewMetadata onToggleSelectAll={noop} actionsLabel={LABELS.bulkActions} actions={actions} density={'normal'} />
  )
}

export function ListItemsHeaderWithBulkActions({
  checkedItems,
  issueNodes,
  setCheckedItems,
  useBulkActions,
  listHasPRs,
  isInOrganization,
}: ListItemsHeaderProps) {
  const {scoped_repository} = useAppPayload<AppPayload>()

  const {addToast, addPersistedToast} = useToastContext()
  const {setBulkJobId, bulkJobId} = useQueryBulk()

  const isIssueNode = (node: ItemNodeType): node is IssueNodeType => node.__typename === 'Issue'
  const issuesToActOn: IssueNodeType[] = Array.from(checkedItems.values())
    .filter(v => v != null)
    .filter(isIssueNode)

  const [isBulkActionRunning, setIsBulkActionRunning] = useState(bulkJobId !== null)
  const {activeSearchQuery, isQueryLoading} = useQuerySearchState()
  const {setMultiPageSelectionAllowed} = useListViewMultiPageSelection()

  const bulkActionCompleted = useCallback(
    (jobId?: string) => {
      setIsBulkActionRunning(false)
      if (jobId) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addPersistedToast({
          type: 'info',
          message: LABELS.updatingIssues,
          icon: <RocketIcon />,
          role: 'status',
        })
        setBulkJobId(jobId)
      }
    },
    [addPersistedToast, setBulkJobId],
  )

  const bulkActionError = useCallback(
    (error: Error) => {
      setIsBulkActionRunning(false)
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({
        type: 'error',
        message: `Could not update issues: ${error.message}`,
      })
    },
    [addToast],
  )

  const [useSearchQueryForBulk, setUseSearchQueryForBulk] = useState(false)
  const sharedActionProps = useMemo(
    () => ({
      onCompleted: bulkActionCompleted,
      onError: bulkActionError,
      disabled: isBulkActionRunning,
      issuesToActOn: useSearchQueryForBulk
        ? issueNodes.filter(node => node != null).map(node => node.id)
        : issuesToActOn.filter(node => node != null).map(node => node.id),
      query: activeSearchQuery,
    }),
    [
      useSearchQueryForBulk,
      bulkActionCompleted,
      bulkActionError,
      isBulkActionRunning,
      issueNodes,
      issuesToActOn,
      activeSearchQuery,
    ],
  )
  const {setSelectedCount} = useListViewSelection()

  const onToggleSelectAll = useCallback(
    (isSelectAllChecked: boolean) => {
      if (isSelectAllChecked) {
        setCheckedItems(
          issueNodes.filter(node => node != null).reduce((map, node) => map.set(node.id, node), new Map()),
        )
      } else {
        setCheckedItems(new Map())
        if (useSearchQueryForBulk) {
          setUseSearchQueryForBulk(false)
          setSelectedCount(0)
          setMultiPageSelectionAllowed?.(false)
        }
      }
    },
    [issueNodes, setCheckedItems, setMultiPageSelectionAllowed, setSelectedCount, useSearchQueryForBulk],
  )

  const shouldRenderBulkActions = scoped_repository && useBulkActions && !listHasPRs

  const analyticsMetadata = useMemo(
    () => ({
      owner: scoped_repository?.owner ?? '',
      repositoryName: scoped_repository?.name ?? '',
    }),
    [scoped_repository?.name, scoped_repository?.owner],
  )
  const [overflowMenuOpen, setOverflowMenuOpen] = useState<boolean | undefined>(undefined)

  const overflowMenuProps = useMemo(() => {
    // see https://github.com/github/primer/issues/5896#issuecomment-3335244847
    const onOpenChange = (open: boolean) => guardOverflowMenuChange(open, setOverflowMenuOpen)
    return {open: overflowMenuOpen, onOpenChange}
  }, [overflowMenuOpen])

  // This array should be in sync with the actions array populating the fallback component
  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const actions = useMemo<Action[] | undefined>(() => {
    if (!shouldRenderBulkActions) return
    const issueIds = issuesToActOn.map(i => i.id)
    const issueNumbers = issuesToActOn.map(i => i.number)
    const actionList = [
      {
        key: 'mark-as',
        render: (isOverflowMenu: boolean) => <BulkMarkAs {...sharedActionProps} nested={isOverflowMenu} />,
      },
      {
        key: 'apply-labels',
        render: (isOverflowMenu: boolean) => (
          <ApplyLabelsBulkAction
            owner={scoped_repository.owner}
            repo={scoped_repository.name}
            nested={isOverflowMenu}
            issueIds={issueIds}
            {...sharedActionProps}
          />
        ),
      },
      {
        key: 'apply-assignees',
        render: (isOverflowMenu: boolean) => (
          <ApplyAssigneesBulkAction
            nested={isOverflowMenu}
            issueIds={issueIds}
            issueNumbers={issueNumbers}
            {...sharedActionProps}
            owner={scoped_repository?.owner}
            repo={scoped_repository?.name}
          />
        ),
      },
      {
        key: 'add-to-projects',
        render: (isOverflowMenu: boolean) => (
          <AddToProjectsBulkAction
            nested={isOverflowMenu}
            issueIds={issuesToActOn.map(i => i.id)}
            owner={scoped_repository?.owner}
            repo={scoped_repository?.name}
            {...sharedActionProps}
          />
        ),
      },
      {
        key: 'apply-milestone',
        render: (isOverflowMenu: boolean) => (
          <ApplyMilestoneBulkAction
            owner={scoped_repository.owner}
            repo={scoped_repository.name}
            nested={isOverflowMenu}
            issueIds={issuesToActOn.map(i => i.id)}
            {...sharedActionProps}
          />
        ),
      },
    ]

    if (isInOrganization)
      actionList.push({
        key: 'apply-issue-type',
        render: (isOverflowMenu: boolean) => (
          <AnalyticsProvider appName="issue_types" category="issues_index" metadata={analyticsMetadata}>
            <ApplyIssueTypeAction
              owner={scoped_repository.owner}
              nested={isOverflowMenu}
              issueIds={issueIds}
              {...sharedActionProps}
            />
          </AnalyticsProvider>
        ),
      })

    return actionList
  }, [
    shouldRenderBulkActions,
    sharedActionProps,
    scoped_repository?.owner,
    scoped_repository?.name,
    isInOrganization,
    issuesToActOn,
    analyticsMetadata,
  ])

  return (
    <Suspense fallback={<ListItemsHeaderBulkFallback />}>
      <ListViewMetadata
        onToggleSelectAll={onToggleSelectAll}
        actionsLabel={LABELS.bulkActions}
        actions={actions}
        density={'normal'}
        assistiveAnnouncement={isQueryLoading ? LABELS.loadingQueryResults : undefined}
        overflowMenuProps={overflowMenuProps}
      />
    </Suspense>
  )
}

try{ ListItemsHeaderBulkFallback.displayName ||= 'ListItemsHeaderBulkFallback' } catch {}
try{ ListItemsHeaderWithBulkActions.displayName ||= 'ListItemsHeaderWithBulkActions' } catch {}